import {
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
import Scrollbar from "src/components/scrollbar";
import ScheduleController from "src/controller/Schedule.controller";
import { PATH_DASHBOARD } from "src/routes/paths";
import StudentTabContainer from "src/pages/student/sections/StudentTabContainer.section";
import {
  format,
  parseISO,
  startOfDay,
  endOfDay,
  isSameDay,
  isAfter,
  isBefore,
} from "date-fns";

const formatDate = (d) =>
  d instanceof Date ? d.toISOString().slice(0, 10) : d;

const formatTime = (val) => {
  if (!val) return "-";
  try {
    const d = typeof val === "string" ? parseISO(val) : val;
    return format(d, "HH:mm");
  } catch {
    return val;
  }
};

const formatTimeLabel = (val) => {
  if (!val) return "";
  try {
    const d = typeof val === "string" ? parseISO(val) : val;
    return format(d, "h:mm a");
  } catch {
    return "";
  }
};

const getClassEndTime = (row) => {
  try {
    const start =
      typeof row.scheduled_at === "string"
        ? parseISO(row.scheduled_at)
        : row.scheduled_at;
    if (!start) return null;
    let minutes;
    if (row.duration_minutes != null) {
      minutes = row.duration_minutes;
    } else {
      const dur = (row.duration || "").toLowerCase();
      const hrMatch = dur.match(/(\d+(?:\.\d+)?)\s*hr/);
      minutes = hrMatch ? Math.round(parseFloat(hrMatch[1]) * 60) : 60;
    }
    return new Date(start.getTime() + minutes * 60 * 1000);
  } catch {
    return null;
  }
};

const formatDateDisplay = (val) => {
  if (!val) return "-";
  try {
    const d = typeof val === "string" ? parseISO(val) : val;
    return format(d, "dd-MM-yyyy");
  } catch {
    return val;
  }
};

/**
 * @param {string|number|null} [studentId] — When set (admin/teacher viewing a profile), classes are loaded for that student via `?student_id=`.
 * @param {string|null} [scheduleHeading] — When `studentId` is set, used as the section title inside the student profile card.
 */
export default function StudentScheduleView({
  studentId = null,
  scheduleHeading = null,
}) {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState("today");
  const [allClasses, setAllClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const viewingOtherStudent =
    studentId != null && String(studentId).trim() !== "";

  useEffect(() => {
    const query = viewingOtherStudent
      ? `?student_id=${encodeURIComponent(String(studentId).trim())}`
      : "";
    setIsLoading(true);
    ScheduleController.getMyClassesStudent(query)
      .then((data) => setAllClasses(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Schedule fetch error:", err);
        setAllClasses([]);
      })
      .finally(() => setIsLoading(false));
  }, [studentId]);

  const now = new Date();
  const startToday = startOfDay(now);

  const previousClasses = allClasses.filter((c) => {
    try {
      const d = typeof c.scheduled_at === "string" ? parseISO(c.scheduled_at) : c.scheduled_at;
      return d && isBefore(d, startToday);
    } catch {
      return false;
    }
  });

  const todayClasses = allClasses.filter((c) => {
    try {
      const d = typeof c.scheduled_at === "string" ? parseISO(c.scheduled_at) : c.scheduled_at;
      return d && isSameDay(d, now);
    } catch {
      return false;
    }
  });

  const upcomingClasses = allClasses.filter((c) => {
    try {
      const d = typeof c.scheduled_at === "string" ? parseISO(c.scheduled_at) : c.scheduled_at;
      return d && isAfter(d, endOfDay(now));
    } catch {
      return false;
    }
  });

  const displayData =
    currentTab === "previous"
      ? previousClasses
      : currentTab === "today"
        ? todayClasses
        : upcomingClasses;

  const showChangedColumn = currentTab === "previous";
  const tableColSpan = showChangedColumn ? 7 : 6;

  const isStudentLayout = window.location.pathname.startsWith("/student");

  const scheduleContent = (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          sx={{ minHeight: 40 }}
        >
          <Tab value="previous" label={`Previous (${previousClasses.length})`} />
          <Tab value="today" label={`Today (${todayClasses.length})`} />
          <Tab value="upcoming" label={`Upcoming (${upcomingClasses.length})`} />
        </Tabs>
        <Typography variant="body2" color="text.secondary">
          {currentTab === "previous"
            ? `${previousClasses.length} previous class${previousClasses.length !== 1 ? "es" : ""}`
            : currentTab === "today"
              ? `${todayClasses.length} class${todayClasses.length !== 1 ? "es" : ""} today`
              : `${upcomingClasses.length} upcoming class${upcomingClasses.length !== 1 ? "es" : ""}`}
        </Typography>
      </Stack>

      <TableContainer
            sx={{
              position: "relative",
              overflow: "unset",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
            }}
          >
            {/* SimpleBar can keep stale table DOM when rows change; remount per sub-tab */}
            <Scrollbar key={`schedule-${currentTab}-${viewingOtherStudent ? String(studentId) : "self"}`}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Enrollment Number
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Teacher
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Program
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "#000",
                        fontWeight: 600,
                      }}
                    >
                      Status
                    </TableCell>
                    {showChangedColumn && (
                      <TableCell
                        sx={{
                          bgcolor: theme.palette.primary.light,
                          color: "#000",
                          fontWeight: 600,
                        }}
                      >
                        Changed
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading &&
                    displayData.map((row, index) => (
                      <TableRow
                        key={
                          row.id != null
                            ? String(row.id)
                            : `${row.scheduled_at ?? "row"}-${index}`
                        }
                        hover
                      >
                        <TableCell>{row.enrollment_number ?? "-"}</TableCell>
                        <TableCell>{row.teacher_name ?? "-"}</TableCell>
                        <TableCell>{row.program_name ?? "-"}</TableCell>
                        <TableCell>{formatDateDisplay(row.scheduled_at)}</TableCell>
                        <TableCell>
                          {(() => {
                            const start = formatTimeLabel(row.scheduled_at);
                            const end = getClassEndTime(row);
                            const endStr = end ? formatTimeLabel(end) : "";
                            return endStr ? `${start} - ${endStr}` : start || "-";
                          })()}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {(() => {
                            const statusKey = (row.status || "").toLowerCase();
                            const isCanceled =
                              statusKey === "canceled" || statusKey === "cancelled";
                            const reasonText =
                              row.reason != null && String(row.reason).trim() !== ""
                                ? String(row.reason).trim()
                                : null;
                            return (
                              <Stack spacing={0.75} alignItems="flex-start">
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: "inline-block",
                                    textTransform: "capitalize",
                                    px: 1,
                                    py: 0.35,
                                    borderRadius: 0.5,
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    ...(statusKey === "present"
                                      ? {
                                          bgcolor: "success.main",
                                          color: "success.contrastText",
                                        }
                                      : statusKey === "absent"
                                        ? {
                                            bgcolor: "warning.main",
                                            color: "warning.contrastText",
                                          }
                                        : isCanceled
                                          ? {
                                              bgcolor: "error.main",
                                              color: "error.contrastText",
                                            }
                                          : {
                                              bgcolor: "action.selected",
                                              color: "text.primary",
                                            }),
                                  }}
                                >
                                  {row.status || "Scheduled"}
                                </Typography>
                                {isCanceled && reasonText ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      maxWidth: 220,
                                      wordBreak: "break-word",
                                      lineHeight: 1.35,
                                    }}
                                  >
                                    {reasonText}
                                  </Typography>
                                ) : null}
                              </Stack>
                            );
                          })()}
                        </TableCell>
                        {showChangedColumn && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {row.is_paid === true ? "Changed" : "Not changed"}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}

                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} sx={{ py: 4 }}>
                        <Typography
                          variant="body2"
                          align="center"
                          color="text.secondary"
                        >
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoading && displayData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} sx={{ py: 6 }}>
                        <Typography
                          variant="body2"
                          align="center"
                          color="text.secondary"
                        >
                          {currentTab === "previous"
                            ? "No previous classes."
                            : currentTab === "today"
                              ? "No classes scheduled for today."
                              : "No upcoming classes."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
    </Stack>
  );

  if (viewingOtherStudent) {
    return (
      <StudentTabContainer title={scheduleHeading || "Schedule"}>
        {scheduleContent}
      </StudentTabContainer>
    );
  }

  if (isStudentLayout) {
    return (
      <StudentTabContainer title="Schedule">
        {scheduleContent}
      </StudentTabContainer>
    );
  }

  return (
    <>
      <CustomBreadcrumbs
        heading="My Schedule"
        links={[
          { name: "Home", href: PATH_DASHBOARD.root },
          { name: "Schedule" },
        ]}
      />
      {scheduleContent}
    </>
  );
}
